import React from 'react';
import {
  Box,
  Typography,
  Slider,
  Button,
  Chip,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { RobotJoint } from '../types';

interface FloatingJointInspectorProps {
  joints: RobotJoint[];
  selectedJoint?: string;
  onSelectJoint: (name: string | undefined) => void;
  jointValues: Record<string, number>;
  onJointValueChange: (name: string, value: number) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onResetAll: () => void;
  articulableCount: number;
}

const FloatingJointInspector: React.FC<FloatingJointInspectorProps> = ({
  joints,
  selectedJoint,
  onSelectJoint,
  jointValues,
  onJointValueChange,
  onResetAll,
  articulableCount,
}) => {
  const articulableJoints = joints.filter(
    (j) => j.type === 'revolute' || j.type === 'prismatic' || j.type === 'continuous',
  );

  return (
    <Box
      sx={{
        width: 300,
        minWidth: 300,
        height: '100%',
        backgroundColor: '#0a0a0a',
        borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Joints ({articulableJoints.length})
        </Typography>
        {articulableCount > 0 && (
          <Button
            size="small"
            startIcon={<RestartAltIcon />}
            onClick={onResetAll}
            sx={{
              color: '#EF8997',
              fontSize: 12,
              px: 1,
              '&:hover': { backgroundColor: 'rgba(239, 137, 151, 0.08)' },
            }}
          >
            Reset All
          </Button>
        )}
      </Box>

      {/* Joint list */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {articulableJoints.map((joint) => {
          const isSelected = joint.name === selectedJoint;
          const value = jointValues[joint.name] ?? 0;
          const isContinuous =
            joint.type === 'continuous' && joint.limits.lower === 0 && joint.limits.upper === 0;

          return (
            <Box
              key={joint.name}
              onClick={() => onSelectJoint(isSelected ? undefined : joint.name)}
              sx={{
                p: 1.5,
                borderRadius: 1,
                cursor: 'pointer',
                backgroundColor: isSelected ? 'rgba(239, 137, 151, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: isSelected
                    ? 'rgba(239, 137, 151, 0.12)'
                    : 'rgba(255, 255, 255, 0.04)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: isSelected ? '#EF8997' : 'rgba(255, 255, 255, 0.9)',
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 12,
                  }}
                >
                  {joint.name}
                </Typography>
                <Chip
                  label={joint.type}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: 10,
                    color: 'rgba(255,255,255,0.5)',
                    borderColor: 'rgba(255,255,255,0.1)',
                  }}
                  variant="outlined"
                />
              </Box>

              {isSelected && (
                <Box
                  onClick={(e) => e.stopPropagation()}
                  sx={{ mt: 1 }}
                >
                  <Slider
                    value={value}
                    min={isContinuous ? -Math.PI : joint.limits.lower}
                    max={isContinuous ? Math.PI : joint.limits.upper}
                    step={0.01}
                    onChange={(_, v) => onJointValueChange(joint.name, v as number)}
                    size="small"
                    sx={{
                      color: '#EF8997',
                      '& .MuiSlider-thumb': { width: 12, height: 12 },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}
                  >
                    {value.toFixed(3)} rad
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}

        {articulableJoints.length === 0 && (
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.4)', p: 2, display: 'block', textAlign: 'center' }}
          >
            No articulable joints found
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default FloatingJointInspector;
